import React, { useEffect, useMemo, useState } from 'react';

import NavigationBar from 'components/NavigationBar';
import { useCSVDownloader } from 'react-papaparse';

// Styling
import styled, { StyledProps } from 'styled-components';
import { getAllMarketplaceProjects } from 'helpers/neo4j/neo4jHelper';
import {
  archiveMarketplaceProject,
  createMarketplaceShortUrl,
  getAllMarketplaceShortUrls,
  getJsonFile,
  publishMarketplaceProject,
  updateMarketplaceProjectTargetLeads
} from 'api';
import { useAuthState } from 'stores/AuthStore';

const Container = styled.div`
  padding: 20px;
  padding-top: 0;
`;

// const MenuButtons = styled.div`
//   display: flex;
//   gap: 10px;
//   margin-bottom: 20px;
// `;

// const Button = styled.button<{ active?: boolean }>`
//   padding: 10px 20px;
//   background: ${({ active }) => (active ? '#007bff' : '#f0f0f0')};
//   color: ${({ active }) => (active ? '#fff' : '#000')};
//   border: none;
//   border-radius: 5px;
//   cursor: pointer;
// `;

const SearchInput = styled.input`
  padding: 10px;
  width: 100%;
  max-width: 400px;
  border: 1px solid #ccc;
  border-radius: 5px;
  margin-right: 15px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TR = styled.tr<{ valid?: boolean }>`
  background: ${({ valid }) => (valid ? '#fff' : '#f8d7da')};
`;

const TH = styled.th<{ sortable?: boolean }>`
  padding: 12px;
  background: #f8f8f8;
  border: 1px solid #ddd;
  cursor: ${({ sortable }) => (sortable ? 'pointer' : 'default')};
`;

const TD = styled.td<StyledProps<{ color?: string }>>`
  padding: 12px;
  border: 1px solid #ddd;

  background: ${({ color }) => color || '#fff'};
`;

const SmallNote = styled.span`
  font-size: 0.9em;
  color: #666;
`;

const HorizontalWrapper = styled.div`
  display: flex;
  flex-flow: row;
  align-items: center;
  margin-bottom: 15px;
`;

const StyledSelect = styled.select`
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px;
  margin-left: 15px;
`;

const UnpublishButton = styled.button`
  padding: 5px 10px;
  background: #dc35467c;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
`;

const UpdateTargetButton = styled.button`
  padding: 5px 10px;
  background: #007bff;
  color: #fff;
  border: none;
  border-radius: 5px;
  font-size: 0.8em;
  cursor: pointer;
`;

const BackdropModal = styled.div`
  position: fixed;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
`;

const Modal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: #fff;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1001;
`;

const Title = styled.h2`
  margin-top: 0;
`;

const ArchiveText = styled.div``;

const ButtonWrapper = styled.div`
  display: flex;
  flex-flow: row;
  margin-top: 15px;
  justify-content: space-between;
`;

const CancelButton = styled.button`
  padding: 5px 10px;
  background: #6c757d;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
`;

const ConfirmButton = styled.button`
  padding: 5px 10px;
  background: #28a745;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
`;

const PublishButton = styled.button`
  padding: 5px 10px;
  background: #007bff;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-left: auto;
`;

const ExportButton = styled.button`
  padding: 5px 10px;
  background: #17a2b8;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-left: 10px;
`;

const VerticalWrapper = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;
  gap: 10px;
`;

const StyledLink = styled.a`
  margin-bottom: 10px;
`;

// const MenuOptions = ['Marketplace projects', 'Vaults'];

const ToolsPage = () => {
  const { CSVDownloader } = useCSVDownloader();

  const { AuthState } = useAuthState();
  const { sessionToken } = AuthState;

  // const [activeMenu, setActiveMenu] = useState(MenuOptions[0]);
  const [search, setSearch] = useState('');

  const [marketplaceProjects, setMarketplaceProjects] = useState([]);
  const [shortUrls, setShortUrls] = useState([]);
  const [projectForms, setProjectForms] = useState([]);

  const [selectedMarketplace, setSelectedMarketplace] = useState('prompto');

  const [projectToUnpublish, setProjectToUnpublish] = useState<any>(null);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isPublishSuccess, setIsPublishSuccess] = useState(false);
  const [publishedProject, setPublishedProject] = useState<any>(null);

  const [showUpdateTargetModal, setShowUpdateTargetModal] = useState(false);
  const [marketplaceProjectToUpdate, setMarketplaceProjectToUpdate] =
    useState<any>(null);

  useEffect(() => {
    getAllMarketplaceProjects(selectedMarketplace).then((projects: any) => {
      setMarketplaceProjects(projects);
    });
  }, [selectedMarketplace, refreshTrigger]);

  useEffect(() => {
    getAllMarketplaceShortUrls(sessionToken).then((result: any) => {
      setShortUrls(result.data.shortUrlList);
    });

    const url = `https://storage.googleapis.com/assets.prompto.com/Marketplace/ProjectForms/projectForms.json?_=${Date.now()}`;
    getJsonFile(url).then((data: any) => {
      setProjectForms(data.data);
    });
  }, [sessionToken]);

  const filteredProjects = useMemo(() => {
    return marketplaceProjects.filter((project: any) =>
      [
        project.objectId,
        project.projectTitle,
        project.vaultObjectId,
        project.vaultName
      ]
        .join(' ')
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [search, marketplaceProjects]);

  const UnpublishProject = (project: any) => {
    archiveMarketplaceProject(
      selectedMarketplace,
      project.objectId,
      project.vaultObjectId,
      sessionToken
    )
      .then(() => {
        setShowWarningModal(false);
        setProjectToUnpublish(null);

        setRefreshTrigger((prev) => prev + 1); // Trigger a refresh to update the project list
      })
      .catch((error) => {
        console.error('Error unpublishing project:', error);
        setShowWarningModal(false);
        setProjectToUnpublish(null);
      });
  };

  const PublishProject = () => {
    setIsPublishing(true);

    const vaultId = (document.getElementById('vaultId') as HTMLInputElement)
      ?.value;
    const projectId = (document.getElementById('projectId') as HTMLInputElement)
      ?.value;
    const shareCode = (document.getElementById('shareCode') as HTMLInputElement)
      ?.value;

    if (vaultId && projectId) {
      publishMarketplaceProject(
        selectedMarketplace,
        projectId,
        vaultId,
        sessionToken
      ).then(() => {
        if (shareCode) {
          createMarketplaceShortUrl(
            selectedMarketplace,
            projectId,
            vaultId,
            shareCode,
            sessionToken
          ).then((response) => {
            console.log(response);
            setPublishedProject(response.data);
            setIsPublishing(false);
            setIsPublishSuccess(true);
            setRefreshTrigger((prev) => prev + 1);
          });
        }
      });
    }
  };
  // H5rgkasGBi - uz87euEJB2

  const getCountryForMarketplace = () => {
    let country = 'be';

    switch (selectedMarketplace) {
      default:
      case 'prompto':
      case 'prompto-staging':
        country = 'be';
        break;
      case 'prompto-ae':
        country = 'ae';
        break;
      case 'prompto-nl':
        country = 'nl';
        break;
    }
    return country;
  };

  const updateTargetLeads = () => {
    const targetLeads = (
      document.getElementById('targetLeads') as HTMLInputElement
    ).value;
    updateMarketplaceProjectTargetLeads(
      marketplaceProjectToUpdate.marketplaceProjectObjectId,
      targetLeads,
      sessionToken
    );
  };

  console.log(filteredProjects);

  return (
    <>
      {showWarningModal && (
        <>
          <Modal>
            <Title>Unpublish Project</Title>
            <ArchiveText>
              Are you sure you want to unpublish the project{' '}
              <strong>{projectToUnpublish?.projectTitle}</strong>?
            </ArchiveText>

            <ButtonWrapper>
              <CancelButton
                onClick={() => {
                  setShowWarningModal(false);
                  setProjectToUnpublish(null);
                }}
              >
                Cancel
              </CancelButton>
              <ConfirmButton
                onClick={() => {
                  UnpublishProject(projectToUnpublish);
                }}
              >
                Confirm
              </ConfirmButton>
            </ButtonWrapper>
          </Modal>
          <BackdropModal />
        </>
      )}
      {showPublishModal && (
        <>
          <Modal>
            <Title>Publish a Project</Title>
            {/* Add form or content for publishing a project */}
            {!isPublishSuccess && (
              <VerticalWrapper>
                <label>Vault Id</label>
                <input id="vaultId"></input>
                <label>Project Id</label>
                <input id="projectId"></input>
                <label>Sharecode</label>
                <input id="shareCode"></input>
              </VerticalWrapper>
            )}
            {!isPublishSuccess && (
              <ButtonWrapper>
                <CancelButton
                  onClick={() => {
                    setShowPublishModal(false);
                    setIsPublishing(false);
                    setIsPublishSuccess(false);
                  }}
                >
                  Cancel
                </CancelButton>
                {!isPublishing && (
                  <ConfirmButton
                    onClick={() => {
                      PublishProject();
                    }}
                  >
                    Publish
                  </ConfirmButton>
                )}
              </ButtonWrapper>
            )}
            {isPublishSuccess && (
              <>
                <VerticalWrapper>
                  <StyledLink
                    href={`https://prompto.com/${getCountryForMarketplace()}/vault/${
                      publishedProject?.vaultObjectId
                    }/project/${publishedProject?.objectId}`}
                    target="_blank"
                  >{`https://prompto.com/${getCountryForMarketplace()}/vault/${
                    publishedProject?.vaultObjectId
                  }/project/${publishedProject?.objectId}`}</StyledLink>
                  <StyledLink
                    href={`https://prompto.com/${getCountryForMarketplace()}/project/${
                      publishedProject?.shareCode
                    }`}
                    target="_blank"
                  >{`https://prompto.com/${getCountryForMarketplace()}/project/${
                    publishedProject?.shareCode
                  }`}</StyledLink>
                </VerticalWrapper>
                <br />
                <CancelButton
                  onClick={() => {
                    setShowPublishModal(false);
                    setIsPublishing(false);
                    setIsPublishSuccess(false);
                  }}
                >
                  Cancel
                </CancelButton>
              </>
            )}
          </Modal>
          <BackdropModal />
        </>
      )}

      {showUpdateTargetModal && (
        <>
          <Modal>
            <Title>Set target leads</Title>
            <VerticalWrapper>
              {marketplaceProjectToUpdate?.projectTitle}
              <label>Target leads</label>
              <input id="targetLeads"></input>
              <ConfirmButton
                onClick={() => {
                  updateTargetLeads();
                  setShowUpdateTargetModal(false);
                }}
              >
                Update
              </ConfirmButton>
            </VerticalWrapper>
          </Modal>
          <BackdropModal />
        </>
      )}

      <Container>
        <NavigationBar />
        {/* <MenuButtons>
          {MenuOptions.map((menu) => (
            <Button
              key={menu}
              active={activeMenu === menu}
              onClick={() => setActiveMenu(menu)}
            >
              {menu}
            </Button>
          ))}
        </MenuButtons> */}
        <HorizontalWrapper>
          <SearchInput
            type="text"
            placeholder="Search by client name, project title, ..."
            value={search}
            onChange={(e: any) => setSearch(e.target.value)}
          />
          <div>Marketplace:</div>
          <StyledSelect
            name="marketplace"
            id="marketplace"
            onChange={(e) => setSelectedMarketplace(e.target.value)}
            value={selectedMarketplace}
          >
            <option value="prompto">Prompto BE</option>
            <option value="staging-prompto">Staging</option>
            <option value="prompto-ae">Prompto UAE</option>
            <option value="prompto-nl">Prompto NL</option>
          </StyledSelect>
          <PublishButton onClick={() => setShowPublishModal(true)}>
            Publish a project
          </PublishButton>
          <ExportButton>
            <CSVDownloader
              data={filteredProjects}
              filename={`all_marketplace_projects_${Date.now()}.csv`}
              bom={true}
            >
              Export
            </CSVDownloader>
          </ExportButton>
        </HorizontalWrapper>
        <Table>
          <thead>
            <tr>
              <TH>Client</TH>
              <TH>Project</TH>
              <TH>URL</TH>
              <TH>Short URL</TH>
              <TH>Form Page</TH>
              <TH>Target leads</TH>
            </tr>
          </thead>
          <tbody>
            {filteredProjects.map((project: any, index: any) => {
              const projectURL = `https://prompto.com/${getCountryForMarketplace()}/vault/${
                project.vaultObjectId
              }/project/${project.objectId}`;

              const shortURLsForProject = shortUrls.filter(
                (url: any) => url.projectObjectId === project.objectId
              );

              return (
                <TR key={index}>
                  <TD>
                    {project.vaultName}{' '}
                    <SmallNote>({project.vaultObjectId})</SmallNote>
                  </TD>
                  <TD>
                    {project.projectTitle}{' '}
                    <SmallNote>({project.objectId})</SmallNote>
                  </TD>
                  <TD>
                    <VerticalWrapper>
                      {project.targetLeads}
                      <UpdateTargetButton
                        onClick={() => {
                          setMarketplaceProjectToUpdate(project);
                          setShowUpdateTargetModal(true);
                        }}
                      >
                        Update
                      </UpdateTargetButton>
                    </VerticalWrapper>
                  </TD>
                  <TD>
                    <a
                      href={projectURL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {projectURL}
                    </a>
                  </TD>
                  <TD>
                    {shortURLsForProject.map((url: any, idx: number) => {
                      const fullUrl = `https://prompto.com/${getCountryForMarketplace()}/project/${
                        url.shareCode
                      }`;

                      return (
                        <div key={idx}>
                          <a
                            href={fullUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {fullUrl}
                          </a>
                        </div>
                      );
                    })}
                  </TD>
                  <TD>
                    {shortURLsForProject
                      .filter((url: any) => {
                        return projectForms.some(
                          (form: any) => form.shareCode === url.shareCode
                        );
                      })
                      .map((url: any, idx: number) => {
                        const fullUrl = `https://prompto.com/${getCountryForMarketplace()}/form/${
                          url.shareCode
                        }`;

                        return (
                          <div key={idx}>
                            <a
                              href={fullUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {fullUrl}
                            </a>
                          </div>
                        );
                      })}
                  </TD>

                  <TD>
                    <UnpublishButton
                      onClick={() => {
                        setProjectToUnpublish(project);
                        setShowWarningModal(true);
                      }}
                    >
                      Unpublish
                    </UnpublishButton>
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      </Container>
    </>
  );
};

export default ToolsPage;
